// TODO: @ekta-dhanuka - Remove this file when Use_CL_Download_Revamp flag is enabled for all. Download functionality moved to DownloadsPopupV2/ClipDownloadPopup.
import { useMemo } from 'react';
import { Downloads } from '@goldcast/api/content';
import { doesClipHavePlayerConfig } from '@goldcast/player-config/src/utils/clipUtils';
import UpscaledVideoToggle from './UpscaledVideoToggle';
import BrandingToggle from '../BrandingToggle';
import ClipCompositionStatus from './ClipCompositionStatus/ClipCompositionStatus';
import useDownloadAsset from '@/hooks/useDownloadAsset';
import ApiButton from '@/components/atoms/ApiButton/ApiButton';
import { DOWNLOADING_LABELS } from '@/Pages/Clip/constants';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import SelectDropdown from '@/components/atoms/SelectDropdown';
import useTranslationState from '@/hooks/useTranslationState';
import useClipCompositionStatus from '@/hooks/useClipCompositionStatus';
import { useClipPendingUpdate } from '@/hooks/useClipPendingUpdate';
import { ClipSyncStatus } from '@/stores/pendingEditsStore';
import { DOWNLOAD_SUCCESS_STATE_DURATION } from '@/components/molecules/DownloadsPopupV2/constants';

export default function ClipShareDownloadTab() {
  const { clipData } = useClipsContext();
  const {
    translatedLanguagesOptionsWithoutAction,
    selectedLanguageName,
    onLanguageSelection,
    hasMultipleTranslatedLanguages
  } = useTranslationState(clipData);

  const hasPlayerConfig = useMemo(() => {
    return doesClipHavePlayerConfig(clipData);
  }, [clipData]);

  const isUpscaleVideoEnabled = useMemo(() => {
    return false;
  }, []);

  const { isDownloadProcessed, setShouldUpscaleVideo, onDownloadClip, updateDownloadState } = useDownloadAsset({
    type: 'CLIP',
    clip: clipData
  });

  const isRecordingsPage = clipData.asset_metadata.is_full_length;

  const isFailedDownloadStatus = (downloadsData: Downloads) => {
    return downloadsData.status === 'FAILED';
  };

  const isLanguageDropdownVisible = useMemo(() => {
    return !isRecordingsPage && hasMultipleTranslatedLanguages;
  }, [isRecordingsPage, hasMultipleTranslatedLanguages]);

  const { showClipCompositionStatus, clipCompositionStatus, showClipCompositionLoader, affectedClipCompositionUsers } =
    useClipCompositionStatus();
  const { status: clipStatus } = useClipPendingUpdate({ clipId: clipData.id });

  const isDownloadDisabled = useMemo(() => {
    const isConfigMissing = !hasPlayerConfig;

    return isConfigMissing || showClipCompositionLoader || clipStatus !== ClipSyncStatus.sync_completed;
  }, [showClipCompositionLoader, clipStatus, hasPlayerConfig]);

  return (
    <div className="mt-2 p-2">
      {showClipCompositionStatus && !showClipCompositionLoader && (
        <ClipCompositionStatus
          clipCompositionStatus={clipCompositionStatus}
          affectedClipCompositionUsers={affectedClipCompositionUsers}
        />
      )}
      <div className="mb-5 grid grid-cols-3 items-center gap-y-2.5 px-1.5 text-sm">
        <div>Resolution</div>
        <div className="col-span-2">
          <div className="w-full rounded-md py-2 text-right focus:outline-none focus:ring-2 focus:ring-slate-950">
            1080p
          </div>
        </div>
        <div>Format</div>
        <div className="col-span-2">
          <div className="w-full rounded-md py-2 text-right focus:outline-none focus:ring-2 focus:ring-slate-950">
            MP4
          </div>
        </div>
        <BrandingToggle />
        {isLanguageDropdownVisible && (
          <>
            <div>Subtitles</div>
            <div className="col-span-2">
              <SelectDropdown
                options={translatedLanguagesOptionsWithoutAction}
                selected={selectedLanguageName}
                onChange={language => onLanguageSelection(language)}
                menuClassName="right-0 left-auto"
                className="ml-auto min-w-[165px] max-w-[240px]"
                placeholder="Select language"
              />
            </div>
          </>
        )}
      </div>
      {isUpscaleVideoEnabled && !isRecordingsPage && (
        <UpscaledVideoToggle onUpscaleChange={val => setShouldUpscaleVideo(val)} />
      )}
      <ApiButton
        action={onDownloadClip}
        disabled={isDownloadDisabled}
        title={
          clipStatus === ClipSyncStatus.sync_failed
            ? 'Changes not saved. Please try again.'
            : clipStatus === ClipSyncStatus.sync_in_progress
              ? 'Hold on, We are saving your changes...'
              : 'Start Download'
        }
        successMessage={!isDownloadProcessed ? 'Processing Started' : 'Success'}
        variation="filled"
        trackingId="download-button"
        buttonClassNames="rounded-lg h-12 text-white mt-3"
        buttonSize="large"
        failMessage="Download failed"
        fullWidth
        isFailedFn={isFailedDownloadStatus}
        inProgressLabels={DOWNLOADING_LABELS}
        onStatusChanged={updateDownloadState}
        completedStateDuration={DOWNLOAD_SUCCESS_STATE_DURATION}
        showLoaderOverride={showClipCompositionLoader || clipStatus === ClipSyncStatus.sync_in_progress}
        syncTitle
      />
    </div>
  );
}
